import { useMemo, useState } from 'react';
import { Box, LinearProgress, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import SouthEastIcon from '@mui/icons-material/SouthEast';
import { bucketTitle, fmtPct, fmtPrice, growthOf, growthTone, signed, sortRows, statsOf, trendTone, winTone } from '../logic';
import { toneColor } from './tone';

const ROW_HEIGHT = 60;
const VISIBLE_ROWS = 5;

// Desktop: 4 columns. Narrow screens: symbol + win on line 1, OHLC + trend on line 2.
const GRID = {
  display: 'grid',
  alignItems: 'center',
  columnGap: 1.5,
  gridTemplateColumns: { xs: 'minmax(0, 1fr) auto', sm: 'minmax(0, 1.25fr) minmax(0, 1.55fr) 96px 78px' },
  gridTemplateAreas: { xs: '"sym win" "ohlc trend"', sm: '"sym ohlc trend win"' },
};

export default function BucketCard({ score, rows, anchorId, live = false }) {
  const theme = useTheme();
  const [sort, setSort] = useState({ key: 'winRate', desc: true });
  const sorted = useMemo(() => sortRows(rows, sort.key, sort.desc), [rows, sort]);
  const stats = useMemo(() => statsOf(rows), [rows]);

  const tone = trendTone(score);
  const accent = toneColor(theme, tone);
  const strength = Math.abs(score);
  const isDark = theme.palette.mode === 'dark';

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, desc: !s.desc } : { key, desc: key !== 'symbol' }));

  return (
    <Paper
      id={anchorId}
      sx={{
        overflow: 'hidden',
        scrollMarginTop: 96,
        borderTop: `3px solid ${accent}`,
        backgroundImage: `linear-gradient(180deg, ${alpha(accent, (isDark ? 0.07 : 0.04) + strength * 0.012)} 0%, transparent 140px)`,
      }}
    >
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2, pt: 1.75, pb: 1.25 }}>
        <Box
          sx={{
            minWidth: 44, height: 44, borderRadius: 2.5, display: 'grid', placeItems: 'center',
            fontWeight: 800, fontSize: 18, letterSpacing: -0.5,
            color: score === 0 ? 'text.secondary' : theme.palette.getContrastText(accent),
            bgcolor: score === 0 ? alpha(theme.palette.text.secondary, 0.12) : alpha(accent, 0.55 + strength * 0.1),
            boxShadow: score === 0 ? 'none' : `0 6px 16px ${alpha(accent, 0.28)}`,
          }}
        >
          {score === 0 ? '0' : signed(score)}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
            {bucketTitle(score)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {stats.count} {stats.count === 1 ? 'symbol' : 'symbols'}
            {stats.avgWinRate != null && <> · avg win {stats.avgWinRate.toFixed(1)}%</>}
          </Typography>
        </Box>
        <StrengthMeter strength={strength} color={accent} />
      </Stack>

      {/* Column headers (sortable) */}
      <Box
        sx={{
          ...GRID, gridTemplateAreas: { xs: '"sym win"', sm: GRID.gridTemplateAreas.sm },
          px: 2, py: 0.75, borderTop: 1, borderBottom: 1, borderColor: 'divider',
          bgcolor: alpha(theme.palette.text.primary, isDark ? 0.04 : 0.025),
        }}
      >
        <HeaderCell area="sym" label="Symbol" sortKey="symbol" sort={sort} onSort={toggleSort} />
        <HeaderCell area="ohlc" label={live ? 'Live · O/Last' : 'Quote · O/Last'} sortKey="growth" sort={sort} onSort={toggleSort} hideXs />
        <HeaderCell area="trend" label={live ? 'Table → Live' : 'Prev → Now'} sortKey="trend" sort={sort} onSort={toggleSort} hideXs />
        <HeaderCell area="win" label="Win %" sortKey="winRate" sort={sort} onSort={toggleSort} align="right" />
      </Box>

      {/* Rows: 5 visible, the rest scroll inside the card */}
      {sorted.length === 0 ? (
        <Box sx={{ height: ROW_HEIGHT * 1.5, display: 'grid', placeItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">No symbols in this bucket</Typography>
        </Box>
      ) : (
        <Box
          sx={{
            maxHeight: { xs: (ROW_HEIGHT + 22) * VISIBLE_ROWS, sm: ROW_HEIGHT * VISIBLE_ROWS },
            overflowY: 'auto',
            scrollbarWidth: 'thin',
            scrollbarColor: `${alpha(accent, 0.45)} transparent`,
          }}
        >
          {sorted.map((r, idx) => (
            <Row key={r.token} row={r} score={score} striped={idx % 2 === 1} />
          ))}
        </Box>
      )}

      {/* Footer: equal-weight open -> close growth of this bucket */}
      <Stack
        direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" rowGap={0.5}
        sx={{ px: 2, py: 1.1, borderTop: 1, borderColor: 'divider', bgcolor: alpha(accent, isDark ? 0.08 : 0.05) }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          {live ? 'Open → Live' : 'Open → Close'} · avg growth
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 700 }}>▲ {stats.advancers}</Typography>
          <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 700 }}>▼ {stats.decliners}</Typography>
          <GrowthPill value={stats.avgGrowth} />
        </Stack>
      </Stack>
    </Paper>
  );
}

function Row({ row, score, striped }) {
  const theme = useTheme();
  const growth = growthOf(row);
  const gColor = toneColor(theme, growthTone(growth));
  const wColor = toneColor(theme, winTone(row.winRate));
  const moved = row.prevTrend == null ? null : score - row.prevTrend;

  return (
    <Box
      sx={{
        ...GRID,
        minHeight: ROW_HEIGHT, px: 2, py: { xs: 1, sm: 0 }, rowGap: 0.5,
        bgcolor: striped ? alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.025 : 0.018) : 'transparent',
        transition: 'background-color .15s',
        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
      }}
    >
      {/* Symbol */}
      <Box sx={{ gridArea: 'sym', minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, letterSpacing: 0.2 }} noWrap>{row.symbol}</Typography>
        <Tooltip title={[row.symbolName, row.exchange].filter(Boolean).join(' · ')} placement="top-start">
          <Typography variant="caption" color="text.secondary" noWrap component="div">{row.symbolName ?? '—'}</Typography>
        </Tooltip>
      </Box>

      {/* Quote: open / high / low / last */}
      <Box sx={{ gridArea: 'ohlc', minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: gColor, fontVariantNumeric: 'tabular-nums' }}>
            {fmtPrice(row.close)}
          </Typography>
          <Box
            component="span"
            sx={{
              px: 0.6, py: 0.1, borderRadius: 1, fontSize: 11, fontWeight: 700, color: gColor,
              bgcolor: alpha(gColor, 0.12), fontVariantNumeric: 'tabular-nums',
            }}
          >
            {fmtPct(growth)}
          </Box>
        </Stack>
        <Typography
          variant="caption" color="text.secondary" noWrap component="div"
          sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 11 }}
        >
          O {fmtPrice(row.open)} · H {fmtPrice(row.high)} · L {fmtPrice(row.low)}
        </Typography>
      </Box>

      {/* Previous -> current trend */}
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ gridArea: 'trend', justifySelf: { xs: 'end', sm: 'start' } }}>
        <ScoreChip value={row.prevTrend} muted />
        <Tooltip title={moved == null ? 'No previous score yet' : moved === 0 ? 'Unchanged' : moved > 0 ? `Up ${moved}` : `Down ${-moved}`}>
          {moved == null || moved === 0 ? (
            <ArrowForwardIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
          ) : moved > 0 ? (
            <NorthEastIcon sx={{ fontSize: 15, color: 'success.main' }} />
          ) : (
            <SouthEastIcon sx={{ fontSize: 15, color: 'error.main' }} />
          )}
        </Tooltip>
        <ScoreChip value={score} />
      </Stack>

      {/* Win % */}
      <Box sx={{ gridArea: 'win', textAlign: 'right', minWidth: 64 }}>
        <Tooltip title={row.totalTrades != null ? `${row.totalTrades} backtest trades` : ''} placement="top-end">
          <Typography variant="body2" sx={{ fontWeight: 800, color: wColor, fontVariantNumeric: 'tabular-nums' }}>
            {row.winRate == null ? '—' : `${row.winRate.toFixed(1)}%`}
          </Typography>
        </Tooltip>
        <LinearProgress
          variant="determinate"
          value={Math.max(0, Math.min(100, row.winRate ?? 0))}
          sx={{
            mt: 0.4, height: 4, borderRadius: 2, bgcolor: alpha(wColor, 0.15),
            '& .MuiLinearProgress-bar': { borderRadius: 2, bgcolor: wColor },
          }}
        />
      </Box>
    </Box>
  );
}

function ScoreChip({ value, muted = false }) {
  const theme = useTheme();
  const color = toneColor(theme, trendTone(value));
  return (
    <Box
      component="span"
      sx={{
        minWidth: 26, height: 22, px: 0.5, borderRadius: 1.25, display: 'inline-grid', placeItems: 'center',
        fontSize: 12, fontWeight: 800, fontVariantNumeric: 'tabular-nums',
        color: value == null ? 'text.disabled' : color,
        bgcolor: value == null ? 'transparent' : alpha(color, muted ? 0.08 : 0.16),
        border: value == null ? `1px dashed ${theme.palette.divider}` : `1px solid ${alpha(color, muted ? 0.2 : 0.35)}`,
      }}
    >
      {value == null ? '—' : signed(value)}
    </Box>
  );
}

export function GrowthPill({ value, large = false }) {
  const theme = useTheme();
  const color = toneColor(theme, growthTone(value));
  const Icon = value == null || value === 0 ? null : value > 0 ? ArrowUpwardIcon : ArrowDownwardIcon;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.25,
        px: large ? 1.25 : 0.9, py: large ? 0.5 : 0.25, borderRadius: 99,
        fontSize: large ? 15 : 12, fontWeight: 800, fontVariantNumeric: 'tabular-nums',
        color, bgcolor: alpha(color, 0.14),
      }}
    >
      {Icon && <Icon sx={{ fontSize: large ? 16 : 13 }} />}
      {fmtPct(value)}
    </Box>
  );
}

function StrengthMeter({ strength, color }) {
  if (strength === 0) return null;
  return (
    <Tooltip title={`Strength ${strength} of 4`}>
      <Stack direction="row" spacing={0.4} alignItems="flex-end" sx={{ height: 20 }}>
        {[1, 2, 3, 4].map((n) => (
          <Box key={n} sx={{ width: 5, height: 5 + n * 3.5, borderRadius: 1, bgcolor: n <= strength ? color : alpha(color, 0.15) }} />
        ))}
      </Stack>
    </Tooltip>
  );
}

function HeaderCell({ area, label, sortKey, sort, onSort, align = 'left', hideXs = false }) {
  const active = sort.key === sortKey;
  return (
    <Box
      component="button"
      type="button"
      onClick={() => onSort(sortKey)}
      sx={{
        gridArea: area, display: hideXs ? { xs: 'none', sm: 'inline-flex' } : 'inline-flex',
        justifySelf: align === 'right' ? 'end' : 'start', alignItems: 'center', gap: 0.25,
        p: 0, border: 0, bgcolor: 'transparent', cursor: 'pointer', font: 'inherit',
        fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase',
        color: active ? 'text.primary' : 'text.secondary',
        '&:hover': { color: 'text.primary' },
      }}
    >
      {label}
      {active && (sort.desc ? <ArrowDownwardIcon sx={{ fontSize: 13 }} /> : <ArrowUpwardIcon sx={{ fontSize: 13 }} />)}
    </Box>
  );
}
